/**
 * Routing über offene Dienste.
 *
 * Auto/Velo/zu Fuss: Valhalla auf dem FOSSGIS-Server der OSM-Community.
 *   Für den Dauerbetrieb auf osm.kgva.ch gehört dort eine eigene Instanz hin
 *   (oder ein Dienst mit Vertrag) – die öffentliche ist für kleine Last gedacht.
 * ÖV: transport.opendata.ch, die offene Schnittstelle auf die Fahrplandaten
 *   der SBB und der übrigen Schweizer Transportunternehmen.
 */
const VALHALLA = 'https://valhalla1.openstreetmap.de/route';
const TRANSPORT = 'https://transport.opendata.ch/v1';

export const MODES = [
  { id: 'auto', label: 'Auto', costing: 'auto' },
  { id: 'bicycle', label: 'Velo', costing: 'bicycle' },
  { id: 'pedestrian', label: 'Zu Fuss', costing: 'pedestrian' },
  { id: 'transit', label: 'ÖV', costing: null }
];

/** Valhalla liefert die Geometrie als Polyline mit sechs Nachkommastellen. */
export function decodePolyline(str, precision = 6) {
  const factor = 10 ** precision;
  const coords = [];
  let index = 0, lat = 0, lng = 0;

  while (index < str.length) {
    let shift = 0, result = 0, byte;
    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    shift = 0;
    result = 0;
    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    coords.push([lng / factor, lat / factor]);
  }
  return coords;
}

export async function routeVehicle({ from, to, costing, signal }) {
  const res = await fetch(VALHALLA, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      locations: [
        { lat: from.lat, lon: from.lng },
        { lat: to.lat, lon: to.lng }
      ],
      costing,
      costing_options: costing === 'bicycle' ? { bicycle: { bicycle_type: 'Hybrid' } } : undefined,
      directions_options: { language: 'de-DE', units: 'kilometers' }
    })
  });
  if (!res.ok) throw new Error(`Routing nicht möglich (${res.status})`);
  const data = await res.json();
  const trip = data.trip;
  if (!trip || trip.status !== 0) throw new Error(trip?.status_message || 'Keine Route gefunden.');

  const coordinates = trip.legs.flatMap((leg) => decodePolyline(leg.shape));
  const steps = trip.legs.flatMap((leg) =>
    leg.maneuvers.map((m) => ({ text: m.instruction, km: m.length }))
  );
  return {
    kind: 'vehicle',
    coordinates,
    distanceKm: trip.summary.length,
    durationMin: Math.round(trip.summary.time / 60),
    steps
  };
}

const coordOf = (p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;

export async function routeTransit({ from, to, signal, limit = 4 }) {
  const params = new URLSearchParams({
    from: from.station || coordOf(from),
    to: to.station || coordOf(to),
    limit: String(limit)
  });
  const res = await fetch(`${TRANSPORT}/connections?${params}`, { signal });
  if (!res.ok) throw new Error(`Fahrplan nicht erreichbar (${res.status})`);
  const data = await res.json();
  const connections = (data.connections || []).map((c) => ({
    departure: c.from.departure,
    arrival: c.to.arrival,
    fromName: c.from.station?.name,
    toName: c.to.station?.name,
    transfers: c.transfers,
    duration: parseDuration(c.duration),
    legs: (c.sections || []).map((s) => ({
      walk: Boolean(s.walk) && !s.journey,
      line: s.journey ? `${s.journey.category || ''}${s.journey.number ? ' ' + s.journey.number : ''}`.trim() : null,
      to: s.journey?.to || s.arrival?.station?.name,
      fromName: s.departure?.station?.name,
      toName: s.arrival?.station?.name,
      departure: s.departure?.departure,
      arrival: s.arrival?.arrival,
      platform: s.departure?.platform,
      coordinates: stopCoordinates(s)
    }))
  }));
  if (!connections.length) throw new Error('Keine Verbindung gefunden.');
  return { kind: 'transit', connections };
}

/** "00d01:01:00" -> Minuten */
function parseDuration(d) {
  const m = /^(\d+)d(\d{2}):(\d{2}):(\d{2})$/.exec(d || '');
  if (!m) return null;
  return Number(m[1]) * 1440 + Number(m[2]) * 60 + Number(m[3]);
}

function stopCoordinates(section) {
  const list = section.journey?.passList || [section.departure, section.arrival];
  return list
    .map((p) => p?.station?.coordinate || p?.location?.coordinate)
    .filter((c) => c && c.x != null && c.y != null)
    .map((c) => [c.y, c.x]); // API: x = Breite, y = Länge
}

/** Alle Teilstrecken einer Verbindung zu einer Linie verbinden */
export function connectionGeometry(connection) {
  return connection.legs.flatMap((l) => l.coordinates);
}
